import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { motion } from "framer-motion";
import roleService from "../../../service/roleService";
import permissionService from "../../../service/permissionService";
import { Permission, Role } from "./types/permissions";
import PermissionsManagement from "./PermissionsManagement";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";

const ViewRolePermissions: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [role, setRole] = useState<Role | null>(null);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const roleId = Number(id);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [roleData, permissionsData] = await Promise.all([
        roleService.getRole(roleId),
        permissionService.getAllPermissions(),
      ]);
      setRole(roleData);
      setAllPermissions(permissionsData);
    } catch (err) {
      console.error("Failed to load role", err);
      setError("Failed to load this role. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!roleId || Number.isNaN(roleId)) {
      setError("Invalid role.");
      setLoading(false);
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  const handleBack = () => navigate("/configurations/manage-roles");

  const handleUpdatePermissions = async (
    updatedRoleId: number,
    permissionIds: number[],
  ) => {
    try {
      await roleService.updateRole(updatedRoleId, {
        permissions: permissionIds,
      });
      showSuccessToast("Permissions saved successfully");
      navigate("/configurations/manage-roles");
    } catch (err) {
      console.error("Failed to save permissions:", err);
      showErrorToast("Failed to save permissions");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !role) {
    return (
      <Box p={3}>
        <Typography color="error" mb={2}>
          {error ?? "Role not found."}
        </Typography>
        <Button variant="outlined" onClick={handleBack}>
          Back to Roles
        </Button>
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <PermissionsManagement
        role={role}
        allPermissions={allPermissions}
        onBack={handleBack}
        onUpdatePermissions={handleUpdatePermissions}
      />
    </motion.div>
  );
};

export default ViewRolePermissions;
